import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// LocalStorage key helpers
const storageKey = (popupId, freq) => `nexus_popup_${popupId}_${freq}`;

function shouldShow(popup) {
  const { popup_id, frequency } = popup;
  if (frequency === 'always') return true;
  if (frequency === 'once') {
    return !localStorage.getItem(storageKey(popup_id, 'once'));
  }
  // session
  return !sessionStorage.getItem(storageKey(popup_id, 'session'));
}

function markShown(popup) {
  const { popup_id, frequency } = popup;
  if (frequency === 'once') {
    localStorage.setItem(storageKey(popup_id, 'once'), '1');
  } else if (frequency === 'session') {
    sessionStorage.setItem(storageKey(popup_id, 'session'), '1');
  }
}

// ── Form renderer ────────────────────────────────────────────────────────────
function PopupForm({ popup, onClose }) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/public/forms/${popup.form_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: Object.entries(values).map(([label, value]) => ({ label, value })),
        }),
      });
      setSubmitted(true);
    } catch {
      // silently pass — don't block UX
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6">
        <p className="text-lg font-semibold text-gray-800">{popup.form_success_message || 'Thank you!'}</p>
        <button onClick={onClose} className="mt-4 text-sm text-gray-500 hover:text-gray-800 underline">Close</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {(popup.form_fields || []).map((field, i) => (
        <div key={i}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.type === 'textarea' ? (
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              required={field.required}
              value={values[field.label] || ''}
              onChange={e => setValues(v => ({ ...v, [field.label]: e.target.value }))}
            />
          ) : (
            <input
              type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={field.required}
              value={values[field.label] || ''}
              onChange={e => setValues(v => ({ ...v, [field.label]: e.target.value }))}
            />
          )}
        </div>
      ))}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors disabled:opacity-60"
      >
        {submitting ? 'Sending…' : (popup.form_submit_text || 'Submit')}
      </button>
    </form>
  );
}

// ── Main modal ───────────────────────────────────────────────────────────────
export default function PagePopupModal({ tenantId, pageId }) {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const params = new URLSearchParams({ tenant_id: tenantId });
        if (pageId) params.append('page_id', pageId);
        const res = await fetch(`${API_URL}/api/public/popups?${params}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.length || cancelled) return;
        const p = data[0];
        if (!shouldShow(p)) return;
        setPopup(p);
        const delay = p.trigger === 'delayed' ? (p.delay_seconds || 3) * 1000 : 0;
        setTimeout(() => {
          if (!cancelled) {
            setVisible(true);
            markShown(p);
          }
        }, delay);
      } catch {
        // don't crash public page
      }
    };

    load();
    return () => { cancelled = true; };
  }, [tenantId, pageId]);

  const handleClose = () => setVisible(false);

  if (!visible || !popup) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[9998] animate-fadeIn"
        onClick={handleClose}
        data-testid="popup-backdrop"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        data-testid="page-popup-modal"
      >
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scaleIn"
          onClick={e => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            data-testid="popup-close-btn"
            className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors shadow-sm"
          >
            <X size={16} />
          </button>

          {/* Announcement type */}
          {popup.type === 'announcement' && (
            <div>
              {popup.image_url && (
                <div className="w-full aspect-video overflow-hidden">
                  <img
                    src={popup.image_url}
                    alt={popup.title || ''}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-6">
                {popup.title && (
                  <h2 className="text-xl font-bold text-gray-900 mb-2">{popup.title}</h2>
                )}
                {popup.body && (
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{popup.body}</p>
                )}
                {popup.cta_text && popup.cta_url && (
                  <a
                    href={popup.cta_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="popup-cta-btn"
                    className="inline-block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-6 rounded-lg text-sm transition-colors"
                  >
                    {popup.cta_text}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Form type */}
          {popup.type === 'form' && (
            <div className="p-6">
              {popup.form_title && (
                <h2 className="text-xl font-bold text-gray-900 mb-4">{popup.form_title}</h2>
              )}
              <PopupForm popup={popup} onClose={handleClose} />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes scaleIn { from { opacity:0; transform:scale(0.95) } to { opacity:1; transform:scale(1) } }
        .animate-fadeIn { animation: fadeIn 0.2s ease }
        .animate-scaleIn { animation: scaleIn 0.25s ease }
      `}</style>
    </>
  );
}
